import { isMaskedSecret, stripMaskedAttrs } from '~/utils/secretMask'

// LINE連携設定（本部設定）の共通ロジック
//
// サーバは秘密項目（line_channel_secret / line_channel_access_token）を
// 「先頭5文字 + ***」にマスクして返す（Headquarter.mask_secret）。
// マスク値をそのまま保存すると実値が壊れるため、サーバ側にも
// 「末尾が *** なら無視」というガードがあるが、フロント側でも送らない。

// CMP に返ってくる秘密項目
// （サーバの MASKED_ATTRS = server/app/controllers/api/cmp/account/headquarter_controller.rb
//   と一致させること）
//
// NOTE: line_channel_access_token は現状サーバの Headquarter::ATTRS に未登録で、
// 保存ループにも show のマスク対象にも乗らないため、実際には効いていない。
// 将来 Messaging API を使うために ATTRS へ追加したとき、
// 自動的にマスク除去が効くよう先回りで入れてある（サーバ側も同様）。
export const LINE_MASKED_ATTRS = [
  'line_channel_secret',
  'line_channel_access_token'
]

// マスク判定の実体は utils/secretMask.js に集約（店舗設定の秘密値と共通）。
// 既存の import 先を壊さないよう再エクスポートする
export { isMaskedSecret }

/**
 * 保存ペイロードから LINE の秘密項目のマスク値を取り除く（元オブジェクトは変更しない）。
 *
 * @param {Object} payload 保存対象の本部オブジェクト
 * @returns {Object} マスク値のキーを削除した新しいオブジェクト
 */
export const stripMaskedSecrets = function (payload) {
  return stripMaskedAttrs(payload, LINE_MASKED_ATTRS)
}

/**
 * LIFFアプリIDの形式チェック（例: 1234567890-abcdefgh）。
 * 任意項目なので空欄は true を返す。
 * vee-validate の liff_id ルール（plugins/vee-validate.js）の実体。
 *
 * @param {*} value 入力値
 * @returns {boolean} 形式が正しい、または空欄なら true
 */
export const isValidLiffId = function (value) {
  if (value === null || value === undefined || value === '') return true
  return /^\d+-[0-9a-zA-Z]+$/.test(String(value))
}

/**
 * LINEチャネルIDの形式チェック（数字のみ）。
 * 任意項目なので空欄は true を返す。
 *
 * @param {*} value 入力値
 * @returns {boolean} 数字のみ、または空欄なら true
 */
export const isValidLineChannelId = function (value) {
  if (value === null || value === undefined || value === '') return true
  return /^\d+$/.test(String(value))
}

/**
 * LIFF エンドポイントURL。
 * 会員アプリ（CLI）のドメイン + /line/ 。CMP のオリジンではない。
 *
 * @param {String} customerUrl 会員アプリのベースURL（process.env.CUSTOMER_URL）
 * @returns {String} 末尾スラッシュ付きのエンドポイントURL
 */
export const liffEndpointUrl = function (customerUrl) {
  const base = (customerUrl || 'https://cl.gyms.jp').replace(/\/+$/, '')
  return `${base}/line/`
}

/**
 * エンドポイントURLがローカル専用（LINE Developers に貼れない）かどうか。
 *
 * 開発環境の既定値は http://localhost:3000 で、LINE からは到達できない。
 * 実機検証では cloudflare tunnel などの公開URLを
 * CUSTOMER_URL_OVERRIDE（cmp/env.development.js）で上書きする必要があるため、
 * 上書き前は画面に注意書きを出す。
 *
 * @param {String} url 判定対象のURL（liffEndpointUrl の戻り値など）
 * @returns {boolean} ローカル専用ホストなら true
 */
export const isLocalEndpointUrl = function (url) {
  if (!url) return false
  return /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\]|0\.0\.0\.0)(:|\/|$)/i.test(
    String(url)
  )
}

/**
 * リッチメニューに貼るURLのオリジン。
 *
 * LINE Developers のミニアプリチャネルが「LIFF URL」として表示するのは
 * `https://miniapp.line.me/<LIFF_ID>` で、ミニアプリの正式なドメインはこちら。
 * 旧ドメイン `https://liff.line.me/<LIFF_ID>` も引き続き動作するが、
 * ミニアプリとして実装している以上は表示・案内とも miniapp.line.me に揃える。
 */
export const LIFF_URL_ORIGIN = 'https://miniapp.line.me'

// リッチメニューに貼るURLの行定義。
// to は会員アプリ側のパス。「新規予約」はレッスン型（/reserve/lesson/）を既定にする。
// 予約タイプ（cli_reserve_priority）は本部ではなく店舗ごとの設定のため、
// 本部設定のこの画面では出し分けず、イベント型の読み替えを注記で案内している。
const RICH_MENU_ITEMS = [
  { key: 'home', label: 'ホーム', to: null },
  { key: 'reserve', label: '新規予約', to: '/reserve/lesson/' },
  { key: 'unlock', label: '解錠', to: '/line/unlock/' }
]

/**
 * リッチメニューに貼る完成URLの一覧を組み立てる。
 *
 * 本部UID・遷移先はクエリではなく「パス」に入れる。
 * LIFF はリッチメニューURLの追加情報をいったん `liff.state` クエリに畳んで
 * エンドポイントURLへ飛ばし、`liff.init()` の実行後（二次リダイレクト後）に
 * 展開する。会員アプリ側は init より前に本部UIDが要る（本部ごとに LIFF ID が
 * 違うため、LIFF ID を引くのに本部UIDが必要）ので、クエリだと間に合わない。
 * パスならエンドポイントURL配下のパスとしてそのまま届く。
 *
 * 生成例（エンドポイントURL = https://cl.gyms.jp/line/ の場合）:
 *   https://miniapp.line.me/<LIFF_ID>/<本部UID>/
 *   → 会員アプリには /line/?liff.state=%2F<本部UID>%2F が届く
 *
 * LIFF はリッチメニューURLの残り（パスもクエリも）を liff.state に畳んで
 * エンドポイントURLへ渡す。会員アプリ側はそれをパースして本部と遷移先を得る。
 *
 * LIFFアプリIDか本部UIDが欠けている場合は、壊れたURLを
 * 店舗にコピーさせないよう空配列を返す。
 *
 * @param {String} liffId LIFFアプリID（edit_headquarter.line_liff_id）
 * @param {String} headquarterUid 本部UID（edit_headquarter.uid）
 * @returns {Array<{key: String, label: String, url: String}>} URL一覧
 */
export const richMenuLinks = function (liffId, headquarterUid) {
  if (!liffId || !headquarterUid) return []

  const base = `${LIFF_URL_ORIGIN}/${liffId}/${encodeURIComponent(
    headquarterUid
  )}/`
  return RICH_MENU_ITEMS.map(item => ({
    key: item.key,
    label: item.label,
    // item.to は '/reserve/lesson/' 形式。base が末尾スラッシュ付きなので
    // 先頭スラッシュを落として連結する（// にならないように）
    url: item.to ? `${base}${item.to.replace(/^\/+/, '')}` : base
  }))
}
